import { AxiosInstance, AxiosRequestConfig } from 'axios';
import jwtDecode from 'jwt-decode';
import { normalizeAxiosError } from './apiError';
import { RefreshOutcome, singleFlightRefresh } from './refreshManager';
import { netDebug } from './netDebug';

interface DecodedToken {
  exp: number;
}

interface RetryConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

interface AuthInterceptorOptions {
  /** Unique key per auth domain (e.g. 'pos' | 'backoffice') */
  key: string;
  getAccessToken: () => string | undefined;
  refresh: () => Promise<RefreshOutcome>;
  onAuthFailure: () => Promise<void> | void;
  /** Extra headers injected on every request (location, etc.) */
  buildHeaders?: () => Record<string, string>;
}

const authFailedKeys = new Set<string>();

/** Call after a successful login/refresh to re-arm the logout guard. */
export function clearAuthFailure(key: string) {
  authFailedKeys.delete(key);
}

/**
 * Attaches request + response interceptors that handle JWT expiry and 401
 * with single-flight refresh. Returns a cleanup function to eject both.
 */
export function attachAuthInterceptors(
  instance: AxiosInstance,
  opts: AuthInterceptorOptions,
) {
  const { key, getAccessToken, refresh, onAuthFailure, buildHeaders } = opts;

  const ensureHeaders = (config: AxiosRequestConfig) => {
    if (!config.headers) {
      config.headers = {};
    }
    return config.headers as Record<string, any>;
  };

  const applyExtraHeaders = (headers: Record<string, any>) => {
    if (buildHeaders) {
      const extra = buildHeaders();
      // Callers may pin a header explicitly on a single request (e.g. a
      // verified X-Location-ID). Never overwrite an explicitly set header;
      // header names are case-insensitive per HTTP semantics.
      const existingKeys = Object.keys(headers).map(k => k.toLowerCase());
      for (const [k, v] of Object.entries(extra)) {
        if (existingKeys.includes(k.toLowerCase())) continue;
        headers[k] = v;
      }
    }
  };

  const fireAuthFailureOnce = async () => {
    if (authFailedKeys.has(key)) return;
    authFailedKeys.add(key);
    await onAuthFailure();
  };

  const requestId = instance.interceptors.request.use(
    async config => {
      const headers = ensureHeaders(config);
      applyExtraHeaders(headers);

      const token = getAccessToken();
      if (token) {
        try {
          const { exp } = jwtDecode<DecodedToken>(token);
          if (exp * 1000 <= Date.now()) {
            const outcome = await singleFlightRefresh(key, refresh);
            if (outcome.kind === 'success') {
              headers['Authorization'] = `Bearer ${outcome.accessToken}`;
            } else if (outcome.kind === 'rejected') {
              await fireAuthFailureOnce();
            }
          } else {
            headers['Authorization'] = `Bearer ${token}`;
          }
        } catch (refreshError) {
          console.warn(`[${key}] Pre-request refresh error (non-fatal):`, refreshError);
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      netDebug.httpStart(
        `${key} ${config.method?.toUpperCase()} ${config.url}`,
      );
      return config;
    },
    error =>
      Promise.reject(
        new Error(error instanceof Error ? error.message : String(error)),
      ),
  );

  const responseId = instance.interceptors.response.use(
    response => {
      netDebug.httpEnd(
        `${key} ${response.config?.method?.toUpperCase()} ${response.config?.url}`,
      );
      return response;
    },
    async (error: any) => {
      netDebug.httpEnd(
        `${key} ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
      );
      const original = error.config as RetryConfig;
      if (
        error?.response?.status === 401 &&
        original &&
        !original._retry
      ) {
        original._retry = true;
        try {
          const outcome = await singleFlightRefresh(key, refresh);
          if (outcome.kind === 'success') {
            const headers = ensureHeaders(original);
            headers['Authorization'] = `Bearer ${outcome.accessToken}`;
            applyExtraHeaders(headers);
            return instance(original);
          }
          if (outcome.kind === 'unreachable') {
            return Promise.reject(
              new Error('Server unreachable — please check your connection'),
            );
          }
        } catch (refreshError) {
          // Refresh threw → transient failure (network, 404, 5xx).
          // Do NOT logout. Just reject the original request so the caller
          // can show an error / retry later.
          console.warn(
            `[${key}] Transient refresh error, NOT logging out | ${original.method?.toUpperCase()} ${original.url}:`,
            refreshError,
          );
          return Promise.reject(normalizeAxiosError(error));
        }
        console.warn(
          `[${key}] 401 auth failure → logout | ${original.method?.toUpperCase()} ${original.url}`,
        );
        await fireAuthFailureOnce();
        return Promise.reject(new Error('Authentication failed'));
      }
      return Promise.reject(normalizeAxiosError(error));
    },
  );

  return () => {
    instance.interceptors.request.eject(requestId);
    instance.interceptors.response.eject(responseId);
    authFailedKeys.delete(key);
  };
}
